import { type FC, useState } from 'react';
import classnames from 'classnames';
import { graphql, useFragment } from 'react-relay';

import { FormattedMessage, FormattedNumber } from 'dibs-react-intl';
import HeadingLevel from 'dibs-controlled-heading/exports/HeadingLevel.tsx';
import { Button } from 'dibs-elements/exports/Button.tsx';
import InfoCircle from 'dibs-icons/exports/InfoCircle.ts';
import { Link } from 'dibs-elements/exports/Link.tsx';
import TradeBenefitsNextStatusTooltip from './TradeBenefitsNextStatusTooltip.tsx';

import { getNextRewardsData } from '../../../sharedRewards/helpers/rewardsStatusHelper.tsx';
import { TRADE_STATUS, type TradeStatusCode } from '../../tradeBenefitsConstants.ts';
import { NEXT_STATUS_BENEFITS } from './nextStatusBenefits.tsx';
import { trackTradeBenefitsEvent } from '../../tradeBenefitsTracking.ts';

import { type TradeBenefitsNextStatus_tradeFirm$key } from './__generated__/TradeBenefitsNextStatus_tradeFirm.graphql.ts';

import styles from './TradeBenefitsNextStatus.scss';
import dibsCss from 'dibs-css';

const CONTAINER_TIER_CLASS: Partial<Record<TradeStatusCode, string>> = {
    [TRADE_STATUS.SILVER]: styles.containerSilver,
    [TRADE_STATUS.GOLD]: styles.containerGold,
    [TRADE_STATUS.PLATINUM]: styles.containerPlatinum,
};

const tradeFirmFragment = graphql`
    fragment TradeBenefitsNextStatus_tradeFirm on TradeFirmType {
        rewards {
            ...rewardsStatusHelper_rewards
        }
    }
`;

const TradeBenefitsNextStatus: FC<{
    tradeFirm: TradeBenefitsNextStatus_tradeFirm$key | null | undefined;
}> = ({ tradeFirm: tradeFirmRef }) => {
    const tradeFirm = useFragment(tradeFirmFragment, tradeFirmRef);
    const rewards = tradeFirm?.rewards;
    const [isTooltipOpen, setIsTooltipOpen] = useState(false);

    if (!rewards) {
        return null;
    }

    const { nextStatus, nextStatusCode, rewardPoints } = getNextRewardsData(rewards);
    const nextStatusBenefits =
        NEXT_STATUS_BENEFITS[nextStatusCode as keyof typeof NEXT_STATUS_BENEFITS];

    if (!nextStatusBenefits) {
        return null;
    }

    const containerTierClass =
        CONTAINER_TIER_CLASS[nextStatusCode as TradeStatusCode] || styles.containerSilver;

    return (
        <div
            className={classnames(
                containerTierClass,
                dibsCss.border,
                dibsCss.border2,
                dibsCss.borderSolid,
                dibsCss.roundedXsmall,
                dibsCss.pMedium
            )}
        >
            <HeadingLevel>
                {Heading => (
                    <Heading
                        className={classnames(
                            styles.title,
                            dibsCss.textHeaderXLargeNormal,
                            dibsCss.m0,
                            dibsCss.mbXsmall
                        )}
                    >
                        <FormattedMessage
                            id="abt.TradeBenefitsNextStatus.title"
                            defaultMessage="Your Next Status: {nextStatus}"
                            values={{
                                nextStatus: (
                                    <span
                                        className={classnames(
                                            dibsCss.inlineFlex,
                                            dibsCss.itemsStart,
                                            dibsCss.gapXxsmall
                                        )}
                                    >
                                        <span
                                            className={classnames(
                                                styles.titleItalic,
                                                dibsCss.textHeaderXLargeItalic
                                            )}
                                        >
                                            {nextStatus}
                                        </span>

                                        <span
                                            className={classnames(
                                                dibsCss.relative,
                                                dibsCss.inlineFlex
                                            )}
                                        >
                                            <Link onClick={() => setIsTooltipOpen(!isTooltipOpen)}>
                                                <InfoCircle variation="14" />
                                            </Link>
                                            {isTooltipOpen && (
                                                <TradeBenefitsNextStatusTooltip
                                                    onClose={() => setIsTooltipOpen(false)}
                                                />
                                            )}
                                        </span>
                                    </span>
                                ),
                            }}
                        />
                    </Heading>
                )}
            </HeadingLevel>
            <div
                className={classnames(
                    styles.description,
                    dibsCss.sassyFontBodyMedium,
                    dibsCss.mbMedium
                )}
            >
                <FormattedMessage
                    id="abt.TradeBenefitsNextStatus.description"
                    defaultMessage="You’re {points} points away from these{break}<nextStatus>-Exclusive Benefits</nextStatus>"
                    values={{
                        points: (
                            <span
                                className={classnames(
                                    dibsCss.sassyFontBodyMediumHeavy,
                                    styles.descriptionBold
                                )}
                            >
                                {rewardPoints}
                            </span>
                        ),
                        nextStatus: content => (
                            <span
                                className={classnames(
                                    dibsCss.sassyFontBodyMediumHeavy,
                                    styles.descriptionBold
                                )}
                            >
                                {nextStatus}
                                {content}
                            </span>
                        ),
                        break: <br />,
                    }}
                />
            </div>
            <div
                className={classnames(
                    dibsCss.flex,
                    dibsCss.flexCol,
                    dibsCss.gapSmall,
                    dibsCss.textBodyMediumHeavy,
                    dibsCss.mbMedium
                )}
            >
                {nextStatusBenefits.map(({ title, icon }, index) => (
                    <div
                        key={index}
                        className={classnames(
                            styles.benefit,
                            dibsCss.flex,
                            dibsCss.itemsCenter,
                            dibsCss.gapSmall,
                            dibsCss.bgWhite,
                            dibsCss.roundedXsmall,
                            dibsCss.pxSmall,
                            dibsCss.pyXsmall
                        )}
                    >
                        <div className={styles.icon}>{icon}</div>
                        <span className={styles.benefitTitle}>{title}</span>
                    </div>
                ))}
            </div>
            <div
                className={classnames(
                    styles.footer,
                    dibsCss.flex,
                    dibsCss.justifyBetween,
                    dibsCss.itemsCenter
                )}
            >
                <span className={dibsCss.sassyFontBodySmall}>
                    <FormattedMessage
                        id="abt.TradeBenefitsNextStatus.earnPoints"
                        defaultMessage="Earn 1 point for every {spend} spent"
                        values={{
                            spend: (
                                <FormattedNumber
                                    value={1}
                                    style="currency"
                                    currency="USD"
                                    maximumFractionDigits={0}
                                />
                            ),
                        }}
                    />
                </span>
                <Button
                    href="/collections/best-sellers/?sort=newest"
                    target="_blank"
                    style="circle"
                    type="standard"
                    className={styles.button}
                    onClick={() =>
                        trackTradeBenefitsEvent({
                            stepInteractionName: 'start shopping clicked',
                            trigger: 'next tier preview',
                        })
                    }
                >
                    <FormattedMessage
                        id="abt.TradeBenefitsNextStatus.earnPoints.button"
                        defaultMessage="Start Shopping"
                    />
                </Button>
            </div>
        </div>
    );
};

export default TradeBenefitsNextStatus;
